import { useMemo } from "react";
import type { CatalogSort } from "../../../../api/configuration";
import { useEnabledItems } from "../../../../hooks/useMenu";

// Подредбата на асортиментите/рецептите в каталога на „Маркиране" — една и
// съща за списъка (вътре във всяка група; групите остават по sortIndex) и за
// плоския грид. Личната настройка catalogSort избира между двете.

type SortableItem = { pluCode: number | null };

/**
 * PLU съвпаденията при търсене: по-късият код първи (точното съвпадение
 * изпреварва префиксните — при търсене „5" PLU 5 излиза преди PLU 500).
 */
export const byPluMatch = (a: SortableItem, b: SortableItem) =>
  String(a.pluCode).length - String(b.pluCode).length ||
  Number(a.pluCode) - Number(b.pluCode);

/**
 * Компаратор за каталога по личната настройка:
 * - "alphabetical" — по името на текущия език (localeCompare);
 * - "plu" — по PLU код нагоре; продаваемите БЕЗ код остават накрая, по име.
 */
export const catalogComparator = <T extends SortableItem>(
  sort: CatalogSort,
  nameOf: (item: T) => string,
  language: string,
) => {
  const byName = (a: T, b: T) =>
    nameOf(a).localeCompare(nameOf(b), language);
  if (sort !== "plu") return byName;
  return (a: T, b: T) => {
    if (a.pluCode === null || b.pluCode === null) {
      if (a.pluCode === b.pluCode) return byName(a, b);
      return a.pluCode === null ? 1 : -1;
    }
    return a.pluCode - b.pluCode || byName(a, b);
  };
};

/**
 * Има ли поне един активен артикул с PLU код — без кодове двете подредби са
 * еднакви и бутонът във футъра (OrderFooter) не се показва.
 */
export const useHasPluCodes = () => {
  const items = useEnabledItems();
  return useMemo(() => items.some((item) => item.pluCode !== null), [items]);
};
